import { Resources } from 'constants/resources';

import React, { useEffect } from 'react';

import { useNormalizeReferences } from 'hooks/useNormalizeReferences';
import { usePermissions } from 'hooks/usePermissions';
import { MerchantShowAccountsCurrencyInput } from 'pages/Merchants/MerchantsShow/MerchantShowAccounts/components/MerchantShowAccountsCurrencyInput/MerchantShowAccountsCurrencyInput';
import { MerchantShowAccountsMerchantCredentialsField } from 'pages/Merchants/MerchantsShow/MerchantShowAccounts/components/MerchantShowAccountsMerchantCredentialsField/MerchantShowAccountsMerchantCredentialsField';
import { MerchantShowAccountsNameInput } from 'pages/Merchants/MerchantsShow/MerchantShowAccounts/components/MerchantShowAccountsNameInput/MerchantShowAccountsNameInput';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { required, useGetList, useRecordContext } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { Tooltip } from 'shared/mui/Tooltip';
import { Typography } from 'shared/mui/Typography';
import {
  AutocompleteInput,
  AutocompleteMultiselectInput,
  BooleanInput,
} from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { MerchantCredentials } from 'types/embedded/merchant';
import { Permission } from 'types/embedded/permission';

import { BoxStyled, InfoIconStyled, JsonInputStyled } from './MerchantShowAccountsForm.styled';
import { MerchantShowAccountsFormSkeleton } from './MerchantShowAccountsFormSkeleton';

type Props = {
  merchantId?: string;
  errors?: string[];
  isEditMode?: boolean;
  merchantIdEnabledDefaultValue?: boolean;
};

export const MerchantShowAccountsForm: React.FC<Props> = ({
  errors,
  merchantId,
  isEditMode,
  merchantIdEnabledDefaultValue,
}) => {
  const translate = useTypedTranslate();

  const record = useRecordContext();
  const { ready } = useNormalizeReferences({
    referenceFields: ['gateways', 'provider', 'currency', 'merchantCredentials'],
    externalRecord: record,
  });

  const { hasResourcePermissions } = usePermissions();
  const { setValue, watch, setError } = useFormContext();
  const { provider, currency, gateways } = watch();
  const gatewayIds: string[] = gateways ?? [];

  const { data: merchantCredentials = [] } = useGetList<MerchantCredentials>(
    Resources.MerchantCredentials,
    {
      filter: { merchant: merchantId, groups: ['reference'] },
      pagination: { page: 1, perPage: 200 },
    },
  );

  useEffect(() => {
    errors?.forEach((error: string) => {
      const [field, message] = error.split(':');
      setError(field, { type: 'custom', message });
    });
  }, [errors, setError]);

  const isFormValuesDenormalized = isEditMode && !ready;

  if (isFormValuesDenormalized) {
    return (
      <Box sx={{ width: '100%' }}>
        <MerchantShowAccountsFormSkeleton />
      </Box>
    );
  }

  return (
    <>
      {hasResourcePermissions('merchant_accounts', [Permission.Full, Permission.Update]) && (
        <Box mb="24px" sx={{ width: 'fit-content' }}>
          <BooleanInput
            defaultValue={true}
            label={translate('app.merchantAccounts.active')}
            size="small"
            source="active"
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', width: '100%' }}>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', paddingRight: '24px', width: '396px' }}
        >
          <MerchantShowAccountsNameInput
            disabled={!provider || !currency}
            label={translate('app.merchantAccounts.name')}
            merchantId={merchantId}
            size="small"
            source="name"
            validate={required()}
            variant="outlined"
          />
          <ReferenceInput perPage={200} reference="providers" source="provider">
            <AutocompleteInput
              filterToQuery={(searchText: string) => ({ name: searchText })}
              fullWidth
              label={translate('app.merchantAccounts.provider')}
              onChange={() => {
                setValue('gateways', []);
                setValue('currency', null);
              }}
              optionText="name"
              optionValue="@id"
              sx={{ marginTop: '0px' }}
              validate={required()}
              variant="outlined"
            />
          </ReferenceInput>
          <ReferenceArrayInput
            filter={{ provider: provider }}
            perPage={200}
            reference="gateways"
            source="gateways"
          >
            <AutocompleteMultiselectInput
              disabled={!provider}
              label={translate('app.merchantAccounts.gateways')}
              name="gateways"
              onChange={() => setValue('currency', null)}
              optionText="name"
              validate={required()}
            />
          </ReferenceArrayInput>
          <MerchantShowAccountsCurrencyInput
            disabled={!provider || !gatewayIds.length}
            gatewayIds={gatewayIds}
          />
          <BoxStyled>
            <BooleanInput
              label={translate('app.merchantAccounts.partner')}
              size="small"
              source="partner"
            />
            <Tooltip
              arrow
              key="info"
              title={
                <Typography fontSize={12}>
                  {translate('app.merchantAccounts.disableBankOperationsHint')}
                </Typography>
              }
            >
              <InfoIconStyled />
            </Tooltip>
          </BoxStyled>
          <BoxStyled>
            <BooleanInput
              defaultValue={merchantIdEnabledDefaultValue}
              label={translate('app.merchantAccounts.useGlobalIdCreate')}
              size="small"
              source="merchantIdEnabled"
            />
            <Tooltip
              arrow
              key="info"
              title={
                <Typography fontSize={12}>
                  {translate('app.merchantAccounts.useMerchantIdForProviderOrdersHint')}
                </Typography>
              }
            >
              <InfoIconStyled />
            </Tooltip>
          </BoxStyled>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '707px' }}>
          <AutocompleteInput
            choices={merchantCredentials}
            helperText={false}
            label={translate('app.merchantAccounts.merchantAccessCredentials')}
            optionText="name"
            optionValue="@id"
            source="merchantCredentials"
            sx={{
              marginBottom: '16px',
              '.MuiFormControl-root': {
                marginTop: 0,
              },
            }}
            variant="outlined"
          />
          <MerchantShowAccountsMerchantCredentialsField />
          <JsonInputStyled
            label={translate('app.merchantAccounts.accountAccessEditorWithParams')}
            source="credentials"
          />
        </Box>
      </Box>
    </>
  );
};
